import { z } from "zod";
import { capabilitySchema } from "./capabilities";
import { userResponseSchema } from "./users";

/**
 * Dashboard authentication contract.
 *
 * Unlike the mobile auth schemas — which stay in `apps/api/src/mobile/schemas/`
 * because no other workspace consumes them — these are shared with the
 * dashboard web app, so they live here. Cross-workspace imports go only
 * through `packages/*`.
 */

export const authResponseSchema = z.object({
  userId: z.string(),
  token: z.string(),
  /** Absolute epoch SECONDS (the access token's `exp` claim), not a duration. */
  tokenExpireAt: z.number(),
});
export type AuthResponse = z.infer<typeof authResponseSchema>;

export const loginRequestSchema = {
  body: z.object({
    email: z.email(),
    password: z.string().min(6),
  }),
  response: {
    200: authResponseSchema,
  },
};

export type LoginBody = z.infer<typeof loginRequestSchema.body>;
export type LoginResponse = z.infer<(typeof loginRequestSchema.response)[200]>;

export const refreshTokenRequestSchema = {
  response: {
    200: authResponseSchema,
  },
};

export type RefreshTokenResponse = z.infer<
  (typeof refreshTokenRequestSchema.response)[200]
>;

export const resetPasswordRequestSchema = {
  // Keyed on email, not a raw userId: the person who forgot their password
  // does not know their id. Always 202, whether or not the account exists —
  // the endpoint must not be an account-enumeration oracle.
  body: z.object({
    email: z.email(),
  }),
  response: {
    202: z.any(),
  },
};

export type ResetPasswordBody = z.infer<typeof resetPasswordRequestSchema.body>;
export type ResetPasswordResponse = z.infer<
  (typeof resetPasswordRequestSchema.response)[202]
>;

/**
 * The authenticated caller: the user row plus the server-computed
 * `capabilities` conclusion (superadmin, isAdmin and the profession matrix
 * already collapsed into one flat list). Clients hydrate the current user from
 * here rather than decoding the JWT (which is only a hint), and they GATE from
 * this list rather than re-evaluating the matrix: a client-side snapshot of
 * the raw inputs goes stale on revoke and grows a new input kind with every
 * scoping pass. Study-scoped conclusions travel on the study itself, as
 * `permissions`.
 */
export const currentUserSchema = userResponseSchema.extend({
  capabilities: z.array(capabilitySchema),
});

export const getMeRequestSchema = {
  response: {
    200: currentUserSchema,
  },
};

export type CurrentUser = z.infer<typeof currentUserSchema>;
